import { cn } from '@/lib/utils';
import { MessageContext } from 'contexts/MessageContext';
import { memo, useContext, useMemo, useRef } from 'react';
import { useRecoilValue } from 'recoil';

import {
  type IAction,
  type IMessageElement,
  type IStep,
  currentThreadIdState,
  useChatData
} from '@chainlit/react-client';

import { FollowUpChips } from '@/components/AnswerBlocks';
import {
  AnswerProgress,
  startedAtOf,
  stepsOf
} from '@/components/AnswerProgress';
import { SignalsStatus, statusPhaseOf } from '@/components/SignalsStatus';
import { StatusContext } from '@/components/runs/StatusContext';
import { DidNotFinish, QueuedNotice } from '@/components/runs/StatusNotices';
import { isActive, useRunState, useRunsLoaded } from '@/components/runs/api';

import { useLayoutMaxWidth } from 'hooks/useLayoutMaxWidth';

import { Messages } from '..';
import { askFollowUp } from '../../MessageComposer';
import { AskFileButton } from './AskFileButton';
import { MessageAvatar } from './Avatar';
import { MessageButtons } from './Buttons';
import { MessageContent } from './Content';
import { MessageTime } from './MessageTime';
import Step from './Step';
import UserMessage from './UserMessage';

interface Props {
  message: IStep;
  elements: IMessageElement[];
  actions: IAction[];
  indent: number;
  isRunning?: boolean;
  isScorable?: boolean;
  scorableRun?: IStep;
}

const EMPTY_ELEMENTS: IMessageElement[] = [];

const Message = memo(
  ({
    message,
    elements,
    actions,
    isRunning,
    indent,
    isScorable,
    scorableRun
  }: Props) => {
    const { allowHtml, cot, latex, renderUserMarkdown, onError } =
      useContext(MessageContext);

    // While a question is waiting, it lives in the bar above the composer with
    // its choices. Rendering it here as well would put the question at the top
    // of the screen and its buttons at the bottom — two halves of one thing.
    // Once answered this message returns, restated with the reading that was
    // chosen, as the record of what happened.
    const { askUser } = useChatData();
    const isPendingQuestion =
      askUser?.spec.type === 'action' && askUser?.spec.step_id === message.id;
    const layoutMaxWidth = useLayoutMaxWidth();
    const contentRef = useRef<HTMLDivElement>(null);
    const isUserMessage = message.type === 'user_message';
    const isStep = !message.type.includes('message');
    // Only keep tool calls if Chain of Thought is tool_call
    const toolCallSkip =
      isStep && cot === 'tool_call' && message.type !== 'tool';

    const hiddenSkip = isStep && cot === 'hidden';

    const skip = toolCallSkip || hiddenSkip;
    const showInputSection = Boolean(message.input && message.showInput);
    const shouldRenderOutput = !showInputSection || Boolean(message.output);

    // A transient status line, not an answer. The backend marks the phase in
    // metadata and leaves the English sentence in the content as the fallback
    // for an older client; once it starts streaming the real answer the mark is
    // cleared, so this stops rendering on its own.
    // ...and only while something is actually running. A status line is
    // persisted with the thread, so on a reload of a conversation whose answer
    // never arrived the marker is still on it. Drawing the turning mark then
    // claims work is in progress when no container is even alive — the thread
    // sat "Reconciling." for twenty minutes after a refresh. Off the live path
    // it falls back to the English sentence in the content, which is what this
    // rendered before the spinner existed.
    //
    // Since answers outlive their tab (parallel chats), "running" also means
    // the conversation's answer is in flight somewhere: this tab may have
    // opened the thread after the question was asked. A status line that is
    // neither is an answer that never came, and says so (DidNotFinish).
    const markedPhase = statusPhaseOf(
      message.metadata as Record<string, any> | undefined
    );
    const status = useContext(StatusContext);
    const chatThreadId = useRecoilValue(currentThreadIdState);
    const currentThreadId = status.threadId || chatThreadId;
    const threadRunState = useRunState(markedPhase ? currentThreadId : null);
    const runsLoaded = useRunsLoaded();
    const isLatestStatus =
      !!markedPhase && status.latestStatusId === message.id;
    const threadLive = isLatestStatus && isActive(threadRunState);
    const statusPhase =
      markedPhase && (isRunning || threadLive) ? markedPhase : undefined;
    // While the answer waits for the user's choice, the bar above the
    // composer is the one thing to look at; a turning mark here would say
    // the opposite.
    const statusHidden =
      !!markedPhase &&
      !isRunning &&
      isLatestStatus &&
      threadRunState === 'needs_choice';
    const statusStale =
      !!markedPhase && !statusPhase && !statusHidden && runsLoaded;

    // The checklist of the answer's real steps (metadata.signals_steps). It
    // belongs to the wait: shown while the status line is live and while the
    // answer types in under it, never on a finished or abandoned answer -- the
    // server strips it from the final update, but a stopped answer keeps it.
    const progressSteps = useMemo(
      () => stepsOf(message.metadata as Record<string, any> | undefined),
      [message.metadata]
    );
    const waitStartedAt = startedAtOf(
      message.metadata as Record<string, any> | undefined
    );
    const showProgress =
      progressSteps.length > 0 &&
      statusPhase !== 'queued' &&
      (!!statusPhase || (!!isRunning && !markedPhase));

    // The backend offers the next question on the answer it belongs to. One
    // today; the shape is a list so a richer answer contract can offer several
    // without changing this.
    const followUps = useMemo(() => {
      const offered = (message.metadata as Record<string, any> | undefined)
        ?.next_question;
      return typeof offered === 'string' && offered.trim()
        ? [offered.trim()]
        : [];
    }, [message.metadata]);

    const userMessageContent = useMemo(
      () => (
        <MessageContent
          elements={EMPTY_ELEMENTS}
          message={message}
          allowHtml={allowHtml}
          latex={latex}
          renderMarkdown={renderUserMarkdown}
        />
      ),
      [message, allowHtml, latex]
    );

    if (isPendingQuestion || statusHidden) return null;

    if (skip) {
      if (!message.steps) {
        return null;
      }
      return (
        <Messages
          messages={message.steps}
          elements={elements}
          actions={actions}
          indent={indent}
          isRunning={isRunning}
          scorableRun={scorableRun}
        />
      );
    }

    return (
      <>
        <div data-step-type={message.type} className="step py-2">
          <div
            className="flex flex-col"
            style={{
              maxWidth: indent ? '100%' : layoutMaxWidth
            }}
          >
            <div
              className={cn('flex flex-grow pb-2')}
              id={`step-${message.id}`}
            >
              {/* User message is displayed differently */}
              {isUserMessage ? (
                <div className="flex flex-col flex-grow max-w-full">
                  <UserMessage message={message} elements={elements}>
                    {userMessageContent}
                  </UserMessage>
                </div>
              ) : (
                <div className="ai-message flex gap-4 max-sm:gap-2.5 w-full">
                  {/* A status line IS the mark — it turns in the avatar's
                      place. Rendering the static avatar as well put two marks
                      side by side, one still and one moving, which read as a
                      rendering fault rather than a spinner. */}
                  {(!isStep || !indent) && !statusPhase ? (
                    <MessageAvatar
                      author={message.metadata?.avatarName || message.name}
                      isError={message.isError}
                      iconName={message.metadata?.icon}
                    />
                  ) : null}
                  {/* Display the step and its children */}
                  {isStep ? (
                    <Step step={message} isRunning={isRunning}>
                      {showInputSection ? (
                        <MessageContent
                          elements={elements}
                          message={message}
                          allowHtml={allowHtml}
                          latex={latex}
                          renderMarkdown={true}
                          sections={['input']}
                        />
                      ) : null}
                      {message.steps ? (
                        <Messages
                          messages={message.steps.filter(
                            (s) => !s.type.includes('message')
                          )}
                          elements={elements}
                          actions={actions}
                          indent={indent + 1}
                          isRunning={isRunning}
                        />
                      ) : null}
                      {shouldRenderOutput ? (
                        <MessageContent
                          ref={contentRef}
                          elements={elements}
                          message={message}
                          allowHtml={allowHtml}
                          latex={latex}
                          renderMarkdown={true}
                          sections={showInputSection ? ['output'] : undefined}
                        />
                      ) : null}
                      <div className="flex items-center flex-wrap gap-2">
                        <MessageButtons
                          message={message}
                          actions={actions}
                          contentRef={contentRef}
                        />
                        <MessageTime timestamp={message.createdAt} />
                      </div>
                    </Step>
                  ) : (
                    // Display an assistant message
                    <div className="flex flex-col items-start min-w-[150px] flex-grow gap-2">
                      {showProgress ? (
                        <AnswerProgress
                          steps={progressSteps}
                          startedAt={waitStartedAt}
                        />
                      ) : null}
                      {statusPhase === 'queued' ? (
                        <QueuedNotice
                          waitingFor={
                            (message.metadata as Record<string, any>)
                              ?.waiting_for
                          }
                          currentThreadId={currentThreadId}
                        />
                      ) : statusPhase ? (
                        <SignalsStatus
                          phase={statusPhase}
                          detail={
                            (message.metadata as Record<string, any>)
                              ?.signals_status_detail
                          }
                          startedAt={waitStartedAt}
                        />
                      ) : statusStale ? (
                        <DidNotFinish
                          onAskAgain={
                            isLatestStatus &&
                            status.question &&
                            !status.readOnly
                              ? () => askFollowUp(status.question!)
                              : undefined
                          }
                        />
                      ) : (
                        <MessageContent
                          ref={contentRef}
                          elements={elements}
                          message={message}
                          allowHtml={allowHtml}
                          latex={latex}
                          renderMarkdown={true}
                        />
                      )}

                      <AskFileButton messageId={message.id} onError={onError} />

                      <div className="flex items-center flex-wrap gap-2">
                        <MessageButtons
                          message={message}
                          actions={actions}
                          run={
                            scorableRun && isScorable ? scorableRun : undefined
                          }
                          contentRef={contentRef}
                        />
                        <MessageTime
                          timestamp={message.createdAt}
                          seconds={
                            (message.metadata as Record<string, any>)
                              ?.answer_seconds
                          }
                        />
                      </div>

                      {!isRunning ? (
                        <FollowUpChips
                          questions={followUps}
                          onPick={askFollowUp}
                        />
                      ) : null}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        {/* Make sure the child assistant messages of a step are displayed at the root level. */}
        {message.steps && isStep ? (
          <Messages
            messages={message.steps.filter((s) => s.type.includes('message'))}
            elements={elements}
            actions={actions}
            indent={0}
            isRunning={isRunning}
            scorableRun={scorableRun}
          />
        ) : null}
        {/* Display the child steps if the message is not a step (usually a user message). */}
        {message.steps && !isStep ? (
          <Messages
            messages={message.steps}
            elements={elements}
            actions={actions}
            indent={indent}
            isRunning={isRunning}
          />
        ) : null}
      </>
    );
  }
);

export { Message };
